import {AfterContentInit, Component, OnDestroy, OnInit, ViewChild}    from '@angular/core';
import {
  ApDynformsConfigFieldset
}                                                                     from '../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  ApDynformsConfigComboBox
}                                                                     from '../../ap-dynforms/config/ap-dynforms-config-combobox';
import {ApDynformsValidator}                                          from '../../ap-dynforms/ap-dynforms-validator';
import {Validators}                                                   from '@angular/forms';
import {ApDynformsComponent}                                          from '../../ap-dynforms/ap-dynforms.component';
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from 'rxjs';
import {IApPermissionData}                                            from '../../ap-interface';
import {SettingsStore}                                                from '../../stores/base-data/settings.store';
import {map}                                                          from 'rxjs/operators';
import {ObjectFactory}                                                from 'ts-tooling';
import {FarmStore}                                                    from '../../stores/farm/farm.store';
import {NotifyStore}                                                  from '../../stores/dialog/notify.store';
import {
  ApDynformsConfigTextbox
}                                                                     from '../../ap-dynforms/config/ap-dynforms-config-textbox';
import {CountryStore}                                                 from '../../stores/common/country.store';
import {DigitItem, ElementOxyde, ISettingsSaveState, SettingsData}    from './types/ap-settings-edit.types';
import {
  ApTranslationService
}                                                                     from '../../ap-utils/service/ap-translation.service';
import {UserStore}                                                    from '../../stores/base-data/user.store';
import {nameOf}                                                       from '../../ap-core/utils/ap-name-of';
import {ApRoleTypeService}                                            from '../../services/common/ap-role-type.service';
import {LoginStore}                                                   from '../../stores/login/login.store';
import {ApPermissions}                                                from '@backend';
import ISettings = Data.BaseData.ISettings;
import IFarm = Data.Authentication.IFarm;
import IUser = Data.Authentication.IUser;
import ICountry = Data.Common.ICountry;
import IFarmUpdate = Data.Authentication.IFarmUpdate;

@Component({
    selector: 'ap-settings-edit',
    template: `
    <ap-dynforms [fieldsets]="formConfig"
                 [caption]="caption"
                 [loading$]="loading$"
                 [headerIcon]="'ap-icon-settings'">
      <div class="ap-form-actions" dynforms.action>
        <button data-cy="setting_general_page_button_submit"
                kendoButton
                autofocus
                class="ap-form-button-right"
                type="submit"
                [disabled]="(dynForm.validChanges | async) !== true || (apPermissions.EDIT_SETTINGS | getPermission | async) !== true || (this.disabledButton.asObservable() | async)"
                [themeColor]="'primary'"
                (apSingleClick)="onSubmit()">
          {{ 'Global__Save' | translate }}
        </button>
      </div>
    </ap-dynforms>`,
    standalone: false
})
export class ApSettingsEditComponent implements OnInit, AfterContentInit, OnDestroy {
  @ViewChild(ApDynformsComponent, {static: true}) dynForm: ApDynformsComponent;
  public loading$: Observable<boolean> = of(true);
  public disabledButton: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public formConfig: ApDynformsConfigFieldset[];
  public caption: string;
  public apPermissions = ApPermissions;
  public apPermissionData: IApPermissionData;

  // use behavior subject to be able to bind observables to the dynform
  // when not using observables, and switching the farm, the dynform validators
  // stopped working because another instance of fieldset has been assigned to the form
  private _data: BehaviorSubject<SettingsData> = new BehaviorSubject<SettingsData>(null);
  private _setting: ISettings = null;
  private _subscriptions: Subscription[] = [];
  private _saveState: ISettingsSaveState;

  constructor(private farmStore: FarmStore,
              private userStore: UserStore,
              private loginStore: LoginStore,
              private notifyStore: NotifyStore,
              private countryStore: CountryStore,
              private settingsStore: SettingsStore,
              private translationService: ApTranslationService) {
  }

  public ngOnInit(): void {
    this.caption = this.translationService.translate('Settings__Label_Settings');
    this.loading$ = combineLatest([
      this.userStore.Loading$,
      this.farmStore.Loading$,
      this.countryStore.Loading$,
      this.settingsStore.Loading$
    ]).pipe(
      map(loadings => loadings.some(loading => loading))
    );
    this.countryStore.loadAllCountries();
    this.formConfig = this._getFieldSets();
  }

  public ngAfterContentInit(): void {
    this._subscriptions.push(
      combineLatest([
        this.settingsStore.FirstSetting$,
        this.farmStore.CurrentFarm$
      ]).subscribe(([setting, farm]) => {
        if (!!setting && !!farm) {
          this._applyFormData(setting, farm);
        }
      })
    );
    this._subscriptions.push(
      combineLatest([
        this.farmStore.Listen(x => x.farmUpdated),
        this.settingsStore.Listen(x => x.settingsSaved)
      ]).subscribe(([farmUpdated, settingsSaved]) => {
        // Check if the save operation finished with true or false.
        // Undefined is the initial value and means the operation did not finish yet
        if (farmUpdated != null) {
          this._saveState.FarmUpdated = farmUpdated;
        }
        if (settingsSaved != null) {
          this._saveState.SettingsSaved = settingsSaved;
        }
        // after collecting the save state
        // => perform afterSave operations to enable/disable the button
        // and show a save success or fail message
        this._onAfterSave();
      })
    );
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach(x => x?.unsubscribe());
  }

  public onSubmit(): void {
    const formData = this.dynForm.form.getRawValue() as SettingsData;
    this.disabledButton.next(true);
    this._saveState = {
      FarmUpdated: undefined,
      SettingsSaved: undefined
    };
    if (this._isFarmDataChanged(formData)) {
      const farmUpdate: IFarmUpdate = {
        Country: this.countryStore.Countries.find(x => x.Id === formData.Country),
        RegistryNumber: formData.RegistryNumber,
        SampleTechnician: formData.SampleTechnician,
        SalesPerson: formData.SalesPerson
      };
      this.farmStore.updateFarm(farmUpdate);
    } else {
      this._saveState.FarmUpdated = true;
    }
    if (this._isSettingsChanged(formData)) {
      this._setting.DigitsAfterDecimalPoint = formData.DigitsAfterDecimalPoint;
      this._setting.FarmTime = formData.FarmTime;
      this._setting.UnitElementOxyd = formData.UnitElementOxyd;
      this.settingsStore.updateSettings(this._setting);
    } else {
      this._saveState.SettingsSaved = true;
    }
    // in case no updates need we need to execute the post save operations at this point
    this._onAfterSave();
  }

  /**
   * Handles post save logic when all settings have been saved
   * - enable/disable the save button
   * - show save success or fail message
   * @private
   */
  private _onAfterSave(): void {
    const saveComplete = this._saveState?.FarmUpdated != null
      && this._saveState?.SettingsSaved != null;
    if (!saveComplete) {
      return;
    }
    // Check if all save operations were successful
    if (!!this._saveState.FarmUpdated && !!this._saveState.SettingsSaved) {
      this.notifyStore.showSaveSuccessMessage();
    } else {
      this.notifyStore.showSaveFailMessage();
    }
    this.disabledButton.next(!saveComplete);
  }

  private _applyFormData(setting: ISettings, farm: IFarm): void {
    this._setting = ObjectFactory.Copy(setting);
    const settingsData: SettingsData = {
      FarmName: farm?.FarmName ? farm.FarmName : ' ',
      CustomerNumber: farm?.CustomerNumber ? farm.CustomerNumber : ' ',
      Street: farm?.Street ? farm.Street : ' ',
      Street2: farm?.Street2 ? farm.Street2 : ' ',
      PostalCodePlusCity: this._getPostCodePlusLocation(farm),
      State: farm?.State ? farm.State : ' ',
      RegistryNumber: farm?.RegistryNumber ? farm.RegistryNumber : ' ',
      DigitsAfterDecimalPoint: setting.DigitsAfterDecimalPoint,
      FarmTime: setting.FarmTime,
      UnitElementOxyd: setting.UnitElementOxyd,
      Country: farm?.Country?.Id,
      SampleTechnician: farm?.SampleTechnician,
      SalesPerson: farm?.SalesPerson
    };
    // use timeout to avoid 'expression has been changed after it was checked' errors
    setTimeout(() => this._data.next(settingsData), 0);
  }

  private _getFieldSets(): ApDynformsConfigFieldset[] {
    return [
      new ApDynformsConfigFieldset({
        key: 'Global__Settings_Address',
        legend: 'Global__Settings_Address',
        config: [
          new ApDynformsConfigTextbox({
            key: nameOf<SettingsData>('CustomerNumber'),
            label: 'Global__Farm_CustomerNumber',
            value$: this._data.pipe(map(x => x?.CustomerNumber)),
            disabled: true
          }),
          new ApDynformsConfigTextbox({
            key: nameOf<SettingsData>('FarmName'),
            label: 'Global__Farm_CustomerName',
            value$: this._data.pipe(map(x => x?.FarmName)),
            disabled: true
          }),
          new ApDynformsConfigTextbox({
            key: nameOf<SettingsData>('Street'),
            label: 'Global__Farm_Street',
            value$: this._data.pipe(map(x => x?.Street)),
            disabled: true
          }),
          new ApDynformsConfigTextbox({
            key: nameOf<SettingsData>('Street2'),
            label: 'Global__Farm_Street2',
            value$: this._data.pipe(map(x => x?.Street2)),
            disabled: true
          }),
          new ApDynformsConfigTextbox({
            key: nameOf<SettingsData>('PostalCodePlusCity'),
            label: 'Global__Farm_PostalCodePlusCity',
            value$: this._data.pipe(map(x => x?.PostalCodePlusCity)),
            disabled: true
          }),
          new ApDynformsConfigTextbox({
            key: nameOf<SettingsData>('State'),
            label: 'Global__Farm_State',
            value$: this._data.pipe(map(x => x?.State)),
            disabled: true
          }),
          new ApDynformsConfigTextbox({
            key: nameOf<SettingsData>('RegistryNumber'),
            label: 'Global__Farm_RegistryNumber',
            value$: this._data.pipe(map(x => x?.RegistryNumber)),
          })
        ]
      }),
      new ApDynformsConfigFieldset({
        key: 'Global__General_Information',
        legend: 'Global__General_Information',
        config: [
          new ApDynformsConfigComboBox({
            key: nameOf<SettingsData>('UnitElementOxyd'),
            label: 'Settings__Lbl_Menu_Unit',
            value$: this._data.pipe(map(x => x?.UnitElementOxyd)),
            valueField: 'Value',
            textField: 'Text',
            options: this._getElementOxydSettings(),
            valuePrimitive: true,
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Select_Category'
              })
            ]
          }),
          new ApDynformsConfigComboBox({
            key: nameOf<SettingsData>('DigitsAfterDecimalPoint'),
            label: 'Global__Display_Number_Decimal_Point',
            value$: this._data.pipe(map(x => x?.DigitsAfterDecimalPoint)),
            valueField: 'Value',
            textField: 'Value',
            options: this._getDigitsAfterDecimalPoint(),
            valuePrimitive: true,
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Select_Category'
              })
            ]
          }),
          new ApDynformsConfigComboBox({
            key: nameOf<SettingsData>('FarmTime'),
            label: 'Base_Weather__LocaleTimeZone',
            value$: this._data.pipe(map(x => x?.FarmTime)),
            valueField: 'Name',
            textField: 'Name',
            options: this.settingsStore.getTimeZones(),
            valuePrimitive: true,
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Select_Category'
              })
            ]
          }),
          new ApDynformsConfigComboBox({
            key: nameOf<SettingsData>('Country'),
            label: 'Global__Country',
            value$: this._data.pipe(map(x => x?.Country)),
            valueField: nameOf<ICountry>('Id'),
            textField: nameOf<ICountry>('Id'),
            options: this.countryStore.Countries$,
            valuePrimitive: true,
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Select_Category'
              })
            ]
          }),
          new ApDynformsConfigComboBox({
            key: nameOf<SettingsData>('SampleTechnician'),
            label: 'SampleTechnician',
            value$: this._data.pipe(map(x => x?.SampleTechnician)),
            valueField: nameOf<IUser>('Id'),
            textField: nameOf<IUser>('FullName'),
            disabled: !ApRoleTypeService.hasAgriconRole(this.loginStore.User),
            options: this.userStore.Users$.pipe(
              map(x => x.filter(x => x.IsConsultant))
            ),
            valuePrimitive: true
          }),
          new ApDynformsConfigComboBox({
            key: nameOf<SettingsData>('SalesPerson'),
            label: 'SalesPerson',
            value$: this._data.pipe(map(x => x?.SalesPerson)),
            valueField: nameOf<IUser>('Id'),
            textField: nameOf<IUser>('FullName'),
            disabled: !ApRoleTypeService.hasAgriconRole(this.loginStore.User),
            options: this.userStore.Users$.pipe(
              map(x => x.filter(x => x.IsConsultant))
            ),
            valuePrimitive: true
          })
        ]
      })
    ];
  }

  private _getPostCodePlusLocation(farm: IFarm): string {
    let postalCodePlusLocation: string;
    if (farm.PostalCode) {
      postalCodePlusLocation = farm.PostalCode;
    }
    if (farm.Location) {
      if (postalCodePlusLocation) {
        postalCodePlusLocation += ' ' + farm.Location;
      } else {
        postalCodePlusLocation = farm.Location;
      }
    }
    if (!postalCodePlusLocation) {
      postalCodePlusLocation = ' ';
    }
    return postalCodePlusLocation;
  }

  private _isFarmDataChanged(formValue: SettingsData): boolean {
    return this._data.value?.RegistryNumber !== formValue.RegistryNumber ||
      this._data.value?.Country !== formValue.Country ||
      this._data.value?.SampleTechnician !== formValue.SampleTechnician ||
      this._data.value?.SalesPerson !== formValue.SalesPerson;
  }

  private _isSettingsChanged(formValue: SettingsData): boolean {
    return this._data.value?.DigitsAfterDecimalPoint !== formValue.DigitsAfterDecimalPoint ||
      this._data.value?.FarmTime !== formValue.FarmTime ||
      this._data.value?.UnitElementOxyd !== formValue.UnitElementOxyd;
  }

  private _getElementOxydSettings(): ElementOxyde[] {
    return [
      {Text: this.translationService.translate('Global__Element_Label'), Value: 1},
      {Text: this.translationService.translate('Global__Oxyd_Label'), Value: 2}
    ];
  }

  private _getDigitsAfterDecimalPoint(): DigitItem[] {
    return [
      {Value: 0},
      {Value: 1},
      {Value: 2},
      {Value: 3},
      {Value: 4},
      {Value: 5},
      {Value: 6}
    ];
  }
}
